import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { LogOut, Menu, X, User, Calendar, Award, HelpCircle, ChevronDown } from 'lucide-react';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { user, activeRole, logout, switchRole } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Check if user has multiple roles for displaying the switcher
  const hasMultipleRoles = user?.roles && user.roles.length > 1;

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsOpen(false);
  };

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="bg-white shadow-lg border-b border-red-100 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Link 
              to="/" 
              className="flex items-center space-x-2 font-display font-bold text-2xl gradient-text hover:opacity-80 transition-opacity"
              data-testid="logo-link"
            >
              <img 
                src="/cibs-logo.png" 
                alt="CIBS Logo" 
                className="w-8 h-8 object-contain"
              />
              <span>DANCE ACADEMY</span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            <Link
              to="/"
              className={`px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                isActive('/') 
                  ? 'text-red-600 bg-red-50 border-b-2 border-red-600' 
                  : 'text-gray-700 hover:text-red-600 hover:bg-red-50'
              }`}
              data-testid="home-nav-link"
            >
              <div className="flex items-center space-x-1">
                <Calendar className="w-4 h-4" />
                <span>Eventi</span>
              </div>
            </Link>

            {user ? (
              <>
                <Link
                  to="/dashboard"
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                    isActive('/dashboard') 
                      ? 'text-red-600 bg-red-50 border-b-2 border-red-600' 
                      : 'text-gray-700 hover:text-red-600 hover:bg-red-50'
                  }`}
                  data-testid="dashboard-nav-link"
                >
                  <div className="flex items-center space-x-1">
                    <User className="w-4 h-4" />
                    <span>Dashboard</span>
                  </div>
                </Link>
                
                <div className="flex items-center space-x-3">
                  {/* Pulsante Assistenza */}
                  <Button
                    onClick={() => window.location.href = 'mailto:s.arrighi@cibsdance.it?subject=Richiesta Assistenza DANCE ACADEMY'}
                    variant="outline"
                    size="sm"
                    className="text-blue-600 border-blue-200 hover:bg-blue-50 hover:border-blue-300"
                    data-testid="assistance-button"
                  >
                    <HelpCircle className="w-4 h-4 mr-1" />
                    Assistenza
                  </Button>

                  <div className="flex flex-col text-right">
                    <span className="text-sm font-medium text-gray-900" data-testid="user-name">
                      {user.name} {user.surname}
                    </span>
                    
                    {/* Role Switcher for users with multiple roles */}
                    {hasMultipleRoles ? (
                      <div className="flex items-center space-x-2">
                        <span className="text-xs text-gray-500">Ruolo attivo:</span>
                        <Select value={activeRole} onValueChange={switchRole}>
                          <SelectTrigger className="h-6 text-xs px-2 py-1 w-auto min-w-24">
                            <SelectValue>
                              <span className={`px-2 py-1 rounded-full text-xs ${
                                activeRole === 'admin' ? 'badge-admin' :
                                activeRole === 'teacher' ? 'badge-teacher' : 
                                activeRole === 'formatore' ? 'badge-formatore' : 
                                activeRole === 'pending' ? 'badge-pending' : 'badge-atleta'
                              }`}>
                                {activeRole === 'pending' ? 'IN ATTESA' : 
                                 activeRole === 'formatore' ? 'FORMATORE' :
                                 activeRole?.toUpperCase()}
                              </span>
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {user.roles.map((role) => (
                              <SelectItem key={role} value={role}>
                                <span className={`px-2 py-1 rounded-full text-xs ${
                                  role === 'admin' ? 'badge-admin' :
                                  role === 'teacher' ? 'badge-teacher' : 
                                  role === 'formatore' ? 'badge-formatore' : 
                                  role === 'pending' ? 'badge-pending' : 'badge-atleta'
                                }`}>
                                  {role === 'pending' ? 'IN ATTESA' : 
                                   role === 'formatore' ? 'FORMATORE' :
                                   role.toUpperCase()}
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ) : (
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        (activeRole || user.role) === 'admin' ? 'badge-admin' :
                        (activeRole || user.role) === 'teacher' ? 'badge-teacher' : 
                        (activeRole || user.role) === 'formatore' ? 'badge-formatore' : 
                        (activeRole || user.role) === 'pending' ? 'badge-pending' : 'badge-atleta'
                      }`} data-testid="user-role-badge">
                        {(activeRole || user.role) === 'pending' ? 'IN ATTESA' : 
                         (activeRole || user.role) === 'formatore' ? 'FORMATORE' :
                         (activeRole || user.role)?.toUpperCase()}
                      </span>
                    )}
                  </div>
                  
                  {!user.approved && user.role !== 'admin' && (
                    <div className="flex items-center space-x-1 text-orange-600 bg-orange-50 px-2 py-1 rounded-md" data-testid="approval-pending">
                      <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
                      <span className="text-xs font-medium">In attesa di approvazione</span>
                    </div>
                  )}
                  
                  <Button
                    onClick={handleLogout}
                    variant="outline"
                    size="sm"
                    className="text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
                    data-testid="logout-button"
                  >
                    <LogOut className="w-4 h-4 mr-1" />
                    Logout
                  </Button>
                </div>
              </>
            ) : (
              <div className="flex items-center space-x-3">
                {/* Pulsante Assistenza */}
                <Button
                  onClick={() => window.location.href = 'mailto:s.arrighi@cibsdance.it?subject=Richiesta Assistenza DANCE ACADEMY'}
                  variant="outline"
                  size="sm"
                  className="text-blue-600 border-blue-200 hover:bg-blue-50 hover:border-blue-300"
                  data-testid="assistance-button-guest"
                >
                  <HelpCircle className="w-4 h-4 mr-1" />
                  Assistenza
                </Button>

                <Link to="/login">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-gray-700 hover:text-red-600 hover:bg-red-50"
                    data-testid="login-nav-button"
                  >
                    Accedi
                  </Button>
                </Link>
                <Link to="/register">
                  <Button
                    size="sm"
                    className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white btn-primary"
                    data-testid="register-nav-button"
                  >
                    Registrati
                  </Button>
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-gray-700 hover:text-red-600 focus:outline-none focus:text-red-600 transition-colors"
              data-testid="mobile-menu-button"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden bg-white border-t border-red-100 animate-fadeIn" data-testid="mobile-menu">
            <div className="px-2 pt-2 pb-3 space-y-1">
              <Link
                to="/"
                onClick={() => setIsOpen(false)}
                className={`block px-3 py-2 rounded-md text-base font-medium transition-all ${
                  isActive('/') 
                    ? 'text-red-600 bg-red-50' 
                    : 'text-gray-700 hover:text-red-600 hover:bg-red-50'
                }`}
                data-testid="mobile-home-link"
              >
                Eventi
              </Link>
              
              {user ? (
                <>
                  <Link
                    to="/dashboard"
                    onClick={() => setIsOpen(false)}
                    className={`block px-3 py-2 rounded-md text-base font-medium transition-all ${
                      isActive('/dashboard') 
                        ? 'text-red-600 bg-red-50' 
                        : 'text-gray-700 hover:text-red-600 hover:bg-red-50'
                    }`}
                    data-testid="mobile-dashboard-link"
                  >
                    Dashboard
                  </Link>
                  
                  <div className="px-3 py-2 border-t border-red-100 mt-2">
                    <div className="text-sm font-medium text-gray-900" data-testid="mobile-user-info">
                      {user.name} {user.surname}
                    </div>
                    <div className="text-xs text-gray-500 mb-2">{user.email}</div>
                    {!user.approved && user.role !== 'admin' && (
                      <div className="text-xs text-orange-600 mb-2" data-testid="mobile-approval-pending">
                        In attesa di approvazione
                      </div>
                    )}
                    
                    <div className="space-y-2">
                      <Button
                        onClick={() => {
                          window.location.href = 'mailto:s.arrighi@cibsdance.it?subject=Richiesta Assistenza DANCE ACADEMY';
                          setIsOpen(false);
                        }}
                        variant="outline"
                        size="sm"
                        className="w-full text-blue-600 border-blue-200 hover:bg-blue-50"
                        data-testid="mobile-assistance-button"
                      >
                        <HelpCircle className="w-4 h-4 mr-1" />
                        Assistenza
                      </Button>

                      <Button
                        onClick={handleLogout}
                        variant="outline"
                        size="sm"
                        className="w-full text-red-600 border-red-200 hover:bg-red-50"
                        data-testid="mobile-logout-button"
                      >
                        <LogOut className="w-4 h-4 mr-1" />
                        Logout
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="px-3 py-2 space-y-2 border-t border-red-100 mt-2">
                  <Button
                    onClick={() => {
                      window.location.href = 'mailto:s.arrighi@cibsdance.it?subject=Richiesta Assistenza DANCE ACADEMY';
                      setIsOpen(false);
                    }}
                    variant="outline"
                    size="sm"
                    className="w-full justify-start text-blue-600 border-blue-200 hover:bg-blue-50"
                    data-testid="mobile-assistance-button-guest"
                  >
                    <HelpCircle className="w-4 h-4 mr-1" />
                    Assistenza
                  </Button>

                  <Link to="/login" onClick={() => setIsOpen(false)}>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full justify-start text-gray-700 hover:text-red-600 hover:bg-red-50"
                      data-testid="mobile-login-button"
                    >
                      Accedi
                    </Button>
                  </Link>
                  <Link to="/register" onClick={() => setIsOpen(false)}>
                    <Button
                      size="sm"
                      className="w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white"
                      data-testid="mobile-register-button"
                    >
                      Registrati
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
